import { type FormEvent, useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  adminCreateService,
  adminDeleteService,
  adminUpdateService,
  fetchAdminServices,
  type AdminServiceCreatePayload,
  type AdminServiceUpdatePayload,
} from "../../lib/api";
import type { Service } from "@statuspage/shared";

const METHODS = ["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"] as const;

const INITIAL_FORM: AdminServiceCreatePayload = {
  name: "",
  url: "",
  method: "GET",
  check_interval_seconds: 60,
  expected_status: 200,
  keyword_match: null,
  timeout_ms: 10000,
  is_active: 1,
  display_order: 0,
  group_name: null,
};

export function ServicesPage(): React.ReactNode {
  const { user } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AdminServiceCreatePayload>(INITIAL_FORM);
  const [editing, setEditing] = useState<string | null>(null);

  const loadServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminServices();
      setServices(response.services);
    } catch {
      setError("Services konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const updateInput = (key: keyof AdminServiceCreatePayload, value: string) => {
    if (key === "method") {
      setForm((current) => ({ ...current, [key]: value }));
      return;
    }

    if (key === "name" || key === "url") {
      setForm((current) => ({ ...current, [key]: value }));
      return;
    }

    if (key === "keyword_match" || key === "group_name") {
      setForm((current) => ({ ...current, [key]: value.trim() === "" ? null : value }));
      return;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    setForm((current) => ({
      ...current,
      [key]: numericValue,
    }));
  };

  const resetForm = () => {
    setEditing(null);
    setForm(INITIAL_FORM);
  };

  const startEdit = (service: Service) => {
    setEditing(service.id);
    setForm({
      name: service.name,
      url: service.url,
      method: service.method,
      check_interval_seconds: service.check_interval_seconds,
      expected_status: service.expected_status,
      keyword_match: service.keyword_match,
      timeout_ms: service.timeout_ms,
      is_active: service.is_active,
      display_order: service.display_order,
      group_name: service.group_name,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      if (editing) {
        const payload: AdminServiceUpdatePayload = {
          name: form.name,
          url: form.url,
          method: form.method,
          check_interval_seconds: form.check_interval_seconds,
          expected_status: form.expected_status,
          keyword_match: form.keyword_match,
          timeout_ms: form.timeout_ms,
          is_active: form.is_active,
          display_order: form.display_order,
          group_name: form.group_name,
        };
        await adminUpdateService(editing, payload);
      } else {
        await adminCreateService(form);
      }
      resetForm();
      await loadServices();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Service konnte nicht gespeichert werden");
    }
  };

  const handleDelete = async (serviceId: string) => {
    if (!confirm("Dienstleister wirklich löschen?")) return;
    try {
      await adminDeleteService(serviceId);
      setServices((current) => current.filter((service) => service.id !== serviceId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Service konnte nicht gelöscht werden");
      await loadServices();
    }
  };

  if (loading) {
    return <div className="px-6 py-8 dark:bg-slate-950 dark:text-slate-100">Lade Services…</div>;
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 dark:bg-slate-950 dark:text-slate-100">
      <h1 className="mb-2 text-2xl font-bold">Services</h1>
      <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">Tenant: {user?.tenant_id}</p>

      {error && <p className="mb-4 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</p>}

      <form onSubmit={handleSubmit} className="mb-8 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2 dark:border-slate-800 dark:bg-slate-900">
        <label className="text-sm" htmlFor="service-name">
          Name
          <input
            id="service-name"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.name}
            onChange={(event) => updateInput("name", event.target.value)}
            required
          />
        </label>
        <label className="text-sm" htmlFor="service-url">
          URL
          <input
            id="service-url"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.url}
            onChange={(event) => updateInput("url", event.target.value)}
            required
          />
        </label>
        <label className="text-sm" htmlFor="service-method">
          Methode
          <select
            id="service-method"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.method}
            onChange={(event) => updateInput("method", event.target.value)}
          >
            {METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm" htmlFor="service-check-interval">
          Prüffrequenz (Sekunden)
          <input
            id="service-check-interval"
            type="number"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.check_interval_seconds}
            onChange={(event) => updateInput("check_interval_seconds", event.target.value)}
            min={1}
            max={86400}
            required
          />
        </label>
        <label className="text-sm" htmlFor="service-expected-status">
          Erwarteter Statuscode
          <input
            id="service-expected-status"
            type="number"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.expected_status}
            onChange={(event) => updateInput("expected_status", event.target.value)}
            min={100}
            max={599}
            required
          />
        </label>
        <label className="text-sm" htmlFor="service-timeout-ms">
          Timeout (ms)
          <input
            id="service-timeout-ms"
            type="number"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.timeout_ms}
            onChange={(event) => updateInput("timeout_ms", event.target.value)}
            min={100}
            max={60000}
            required
          />
        </label>
        <label className="text-sm" htmlFor="service-keyword-match">
          Keyword Match
          <input
            id="service-keyword-match"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.keyword_match ?? ""}
            onChange={(event) => updateInput("keyword_match", event.target.value)}
            placeholder="optional"
          />
        </label>
        <label className="text-sm" htmlFor="service-group-name">
          Gruppenname
          <input
            id="service-group-name"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.group_name ?? ""}
            onChange={(event) => updateInput("group_name", event.target.value)}
            placeholder="optional"
          />
        </label>
        <label className="text-sm" htmlFor="service-is-active">
          Aktiv (1/0)
          <input
            id="service-is-active"
            type="number"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.is_active}
            onChange={(event) => updateInput("is_active", event.target.value)}
            min={0}
            max={1}
          />
        </label>
        <label className="text-sm" htmlFor="service-display-order">
          Reihenfolge
          <input
            id="service-display-order"
            type="number"
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.display_order}
            onChange={(event) => updateInput("display_order", event.target.value)}
            min={0}
          />
        </label>

        <div className="col-span-2 flex gap-2">
          <button
            type="submit"
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
          >
            {editing ? "Speichern" : "Erstellen"}
          </button>
          {editing && (
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-900 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
              onClick={resetForm}
            >
              Abbrechen
            </button>
          )}
        </div>
      </form>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Dienstliste</h2>
        <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-800">
          <table className="min-w-full border-separate border-spacing-0 border border-slate-300 dark:border-slate-700">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800">
                <th scope="col" className="border border-slate-200 p-2 text-left text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  Name
                </th>
                <th scope="col" className="border border-slate-200 p-2 text-left text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  URL
                </th>
                <th scope="col" className="border border-slate-200 p-2 text-left text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  Status
                </th>
                <th scope="col" className="border border-slate-200 p-2 text-left text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  Aktiv
                </th>
                <th scope="col" className="border border-slate-200 p-2 text-left text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service.id}>
                  <td className="border border-slate-200 p-2 text-slate-900 dark:border-slate-700 dark:text-slate-100">
                    {service.name}
                  </td>
                  <td className="border border-slate-200 p-2 text-slate-900 dark:border-slate-700 dark:text-slate-100">
                    {service.url}
                  </td>
                  <td className="border border-slate-200 p-2 text-slate-900 dark:border-slate-700 dark:text-slate-100">
                    {service.method}
                  </td>
                  <td className="border border-slate-200 p-2 text-slate-900 dark:border-slate-700 dark:text-slate-100">
                    {service.is_active ? "ja" : "nein"}
                  </td>
                  <td className="border border-slate-200 p-2 text-slate-900 dark:border-slate-700 dark:text-slate-100">
                    <button
                      type="button"
                      className="text-blue-600 dark:text-blue-400"
                      onClick={() => startEdit(service)}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="ml-2 text-red-600 dark:text-red-400"
                      onClick={() => handleDelete(service.id)}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
